import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardFacade, DashboardResumo } from '../facades/DashboardFacade';
import Sidebar from '../components/Sidebar';

export const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const [resumo, setResumo] = useState<DashboardResumo | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const carregarDados = async () => {
            try {
                const dados = await DashboardFacade.obterResumo();
                setResumo(dados);
            } catch (error) {
                console.error("Erro ao carregar resumo do Dashboard", error);
            } finally {
                setLoading(false);
            }
        };
        carregarDados();
    }, []);

    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
    };

    return (
        <Sidebar>
            <header className="mb-10 border-b-2 border-gray-200 pb-4">
                <h2 className="text-4xl font-black text-gray-800 tracking-tight">Painel de Controle</h2>
                <p className="text-gray-500 font-medium mt-2 text-lg">Visão estratégica e financeira do sistema.</p>
            </header>

            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-900"></div>
                </div>
            ) : (
                <>
                    {/* SEÇÃO DOS CARDS (Existente) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
                        {/* CARD 1: VALOR TOTAL */}
                        <div onClick={() => navigate('/processos')} className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl p-6 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer border border-blue-600 group">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-blue-100 font-bold uppercase tracking-wider text-sm">Risco Financeiro</h3>
                                <span className="text-3xl group-hover:scale-110 transition-transform">💰</span>
                            </div>
                            <p className="text-3xl font-black text-white truncate" title={formatarMoeda(resumo?.valorTotalCausas || 0)}>
                                {formatarMoeda(resumo?.valorTotalCausas || 0)}
                            </p>
                            <p className="text-blue-200 text-xs mt-2 font-medium">Soma do valor das causas</p>
                        </div>

                        {/* CARD 2: PROCESSOS */}
                        <div onClick={() => navigate('/processos')} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer border border-gray-100 group">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">Processos NPU</h3>
                                <span className="text-3xl group-hover:scale-110 transition-transform">⚖️</span>
                            </div>
                            <p className="text-4xl font-black text-blue-900">{resumo?.totalProcessos || 0}</p>
                            <p className="text-gray-400 text-xs mt-2 font-medium">Ações ativas na base</p>
                        </div>

                        {/* CARD 3: CONTRATOS */}
                        <div onClick={() => navigate('/contratos')} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer border border-gray-100 group">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">Contratos</h3>
                                <span className="text-3xl group-hover:scale-110 transition-transform">📄</span>
                            </div>
                            <p className="text-4xl font-black text-blue-900">{resumo?.totalContratos || 0}</p>
                            <p className="text-gray-400 text-xs mt-2 font-medium">Instrumentos vigentes</p>
                        </div>

                        {/* CARD 4: CLIENTES */}
                        <div onClick={() => navigate('/clientes')} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer border border-gray-100 group">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">Órgãos / Clientes</h3>
                                <span className="text-3xl group-hover:scale-110 transition-transform">🏢</span>
                            </div>
                            <p className="text-4xl font-black text-blue-900">{resumo?.totalClientes || 0}</p>
                            <p className="text-gray-400 text-xs mt-2 font-medium">Entidades cadastradas</p>
                        </div>

                        {/* CARD 5: USUÁRIOS */}
                        <div onClick={() => navigate('/usuarios')} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer border border-gray-100 group">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-gray-500 font-bold uppercase tracking-wider text-sm">Usuários</h3>
                                <span className="text-3xl group-hover:scale-110 transition-transform">👥</span>
                            </div>
                            <p className="text-4xl font-black text-blue-900">{resumo?.totalUsuarios || 0}</p>
                            <p className="text-gray-400 text-xs mt-2 font-medium">Contas no sistema</p>
                        </div>
                    </div>

                    {/* NOVA SEÇÃO: BUSCA INTELIGENTE (RAG) */}
                    <div className="mt-10 bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
                        <div className="flex items-center gap-3 mb-4">
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Busca Inteligente (RAG)
                            </h2>
                            <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded border border-yellow-200">
                                Em implementação...
                            </span>
                        </div>

                        <p className="text-sm text-gray-500 mb-4">
                            Em breve, nossa IA jurídica conectada ao Claude analisará seus contratos e processos para responder às suas perguntas instantaneamente.
                        </p>

                        {/* Formulário Bloqueado */}
                        <div className="opacity-60 cursor-not-allowed">
                            <textarea
                                className="w-full p-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed resize-none focus:outline-none focus:ring-0"
                                rows={3}
                                placeholder="Ex: Faça um resumo dos processos ativos na fase de recurso envolvendo o cliente X..."
                                disabled
                                readOnly
                            ></textarea>

                            <div className="flex justify-end mt-3">
                                <button
                                    disabled
                                    className="px-6 py-2 bg-gray-400 text-white font-semibold rounded-lg cursor-not-allowed flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    Pesquisar com IA
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Sidebar>
    );
};